import type { PricingMode } from './constants';

/** "$40" for whole dollars, "$40.50" otherwise. */
export function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** The price label buyers see on a tile or item page. */
export function priceLabel(mode: PricingMode, priceCents: number | null): string {
  switch (mode) {
    case 'FREE':
      return 'Free';
    case 'MAKE_OFFER':
      return priceCents === null ? 'Make offer' : `${formatDollars(priceCents)} or best offer`;
    case 'FIXED':
      return priceCents === null ? 'Make offer' : formatDollars(priceCents);
  }
}

/** Parses what a seller types in the price box ("40", "$40", "40.50") into cents, or null if invalid. */
export function parseDollarsToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

/** What to call an item: its title, or "Item #12" when the seller skipped the title. */
export function itemTitle(item: { number: number; title: string }): string {
  return item.title.trim() || `Item #${item.number}`;
}

/** "#12 Oak dresser", or "Item #12" for an untitled item (never "#12 Item #12"). */
export function itemLabel(item: { number: number; title: string }): string {
  return item.title.trim() ? `#${item.number} ${item.title.trim()}` : `Item #${item.number}`;
}

/**
 * Turns the add-item price box into a pricing mode. The seller types a price and can tap
 * "Free" or "or best offer": a $0 price is free, and a blank price with "or best offer" is
 * "Make offer". Returns null when a price is still needed.
 */
export function pricingFromEntry(entry: { cents: number | null; free: boolean; offers: boolean }):
  { pricingMode: PricingMode; priceCents: number | null } | null {
  if (entry.free || entry.cents === 0) return { pricingMode: 'FREE', priceCents: null };
  if (entry.offers) return { pricingMode: 'MAKE_OFFER', priceCents: entry.cents };
  if (entry.cents === null) return null;
  return { pricingMode: 'FIXED', priceCents: entry.cents };
}
